import { spring } from '@/lib/springs';
import { cn } from '@/lib/utils';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { motion, useReducedMotion } from 'framer-motion';
import * as React from 'react';

const Tabs = TabsPrimitive.Root;

interface IndicatorRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const useActiveTabRect = (
  listRef: React.RefObject<HTMLDivElement>,
  enabled: boolean
) => {
  const [rect, setRect] = React.useState<IndicatorRect | null>(null);

  React.useLayoutEffect(() => {
    const list = listRef.current;
    if (!enabled || !list) return;

    const measure = () => {
      const active = list.querySelector<HTMLElement>(
        '[role="tab"][data-state="active"]'
      );
      const next = active
        ? {
            x: active.offsetLeft,
            y: active.offsetTop,
            width: active.offsetWidth,
            height: active.offsetHeight
          }
        : null;
      setRect((previous) =>
        previous &&
        next &&
        previous.x === next.x &&
        previous.y === next.y &&
        previous.width === next.width &&
        previous.height === next.height
          ? previous
          : next
      );
    };

    measure();
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(list, {
      attributes: true,
      attributeFilter: ['data-state'],
      subtree: true
    });
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(list);
    list
      .querySelectorAll('[role="tab"]')
      .forEach((tab) => resizeObserver.observe(tab));

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [listRef, enabled]);

  return rect;
};

interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  indicatorClassName?: string;
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, indicatorClassName, children, ...props }, forwardedRef) => {
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const hasIndicator = indicatorClassName !== undefined;
  const rect = useActiveTabRect(listRef, hasIndicator);
  const reduceMotion = useReducedMotion();

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef]
  );

  return (
    <TabsPrimitive.List
      ref={setRefs}
      className={cn(
        'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
        hasIndicator && 'relative',
        className
      )}
      {...props}
    >
      {hasIndicator && rect && (
        <motion.span
          aria-hidden
          initial={false}
          animate={{
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height
          }}
          transition={reduceMotion ? { duration: 0 } : spring.moderate}
          className={cn(
            'pointer-events-none absolute top-0 left-0',
            indicatorClassName
          )}
        />
      )}
      {children}
    </TabsPrimitive.List>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs',
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
